/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * Enum Display Helpers
 * Utility functions to convert numeric enums from API to human-readable display strings
 *
 * IMPORTANT: These are DISPLAY-ONLY helpers. The underlying data uses numeric enums.
 * Source: QuickLinks.postman_collection.json enum mappings
 */

import { UtmParams } from "../interfaces/quick-link-list-item.interface";

// ============================================================================
// ACTION TYPE HELPERS
// ============================================================================

/**
 * Get display label for action type
 * @param actionType - 0=Resume, 1=Pause, 2=ProcessNow, 3=Reactivate
 */
export function getActionTypeLabel(actionType: 0 | 1 | 2 | 3): string {
  const labels: Record<number, string> = {
    0: "Resume",
    1: "Pause",
    2: "Process Now",
    3: "Reactivate",
  };
  return labels[actionType] || "Unknown";
}

/**
 * Get PrimeNG severity for action type badge
 * @param actionType - 0=Resume, 1=Pause, 2=ProcessNow, 3=Reactivate
 */
export function getActionTypeSeverity(
  actionType: 0 | 1 | 2 | 3,
): "success" | "warning" | "info" | "danger" | "secondary" {
  const severities: Record<
    number,
    "success" | "warning" | "info" | "danger" | "secondary"
  > = {
    0: "success", // Resume - green
    1: "warning", // Pause - yellow
    2: "info", // Process Now - blue
    3: "danger", // Reactivate - red
  };
  return severities[actionType] || "secondary";
}

/**
 * Get PrimeIcons icon for action type
 * @param actionType - 0=Resume, 1=Pause, 2=ProcessNow, 3=Reactivate
 */
export function getActionTypeIcon(actionType: 0 | 1 | 2 | 3): string {
  const icons: Record<number, string> = {
    0: "pi pi-play",
    1: "pi pi-pause",
    2: "pi pi-bolt",
    3: "pi pi-refresh",
  };
  return icons[actionType] || "pi pi-circle";
}

// ============================================================================
// STATUS HELPERS
// ============================================================================

/**
 * Get display label for status
 * @param status - 0=Disabled, 1=Enabled, 2=Deleted
 */
export function getStatusLabel(status: 0 | 1 | 2): string {
  const labels: Record<number, string> = {
    0: "Disabled",
    1: "Enabled",
    2: "Deleted",
  };
  return labels[status] || "Unknown";
}

/**
 * Get PrimeNG severity for status badge
 * @param status - 0=Disabled, 1=Enabled, 2=Deleted
 */
export function getStatusSeverity(
  status: 0 | 1 | 2,
): "success" | "warning" | "danger" | "secondary" {
  const severities: Record<
    number,
    "success" | "warning" | "danger" | "secondary"
  > = {
    0: "secondary", // Disabled - grey
    1: "success", // Enabled - green
    2: "danger", // Deleted - red
  };
  return severities[status] || "secondary";
}

// ============================================================================
// USAGE LIMIT TYPE HELPERS
// ============================================================================

/**
 * Get display label for usage limit type
 * @param type - 0=SingleUse, 1=LimitedUse, 2=Unlimited
 */
export function getUsageLimitTypeLabel(type: 0 | 1 | 2): string {
  const labels: Record<number, string> = {
    0: "Single Use",
    1: "Limited Use",
    2: "Unlimited",
  };
  return labels[type] || "Unknown";
}

/**
 * Get short label for usage limit type (for compact displays)
 * @param type - 0=SingleUse, 1=LimitedUse, 2=Unlimited
 */
export function getUsageLimitTypeShortLabel(type: 0 | 1 | 2): string {
  const labels: Record<number, string> = {
    0: "Single",
    1: "Limited",
    2: "∞",
  };
  return labels[type] || "?";
}

// ============================================================================
// REDIRECT TYPE HELPERS
// ============================================================================

/**
 * Get display label for redirect type
 * @param type - 0=ThankYouPage, 1=V2Portal, 2=CustomUrl
 */
export function getRedirectTypeLabel(type: 0 | 1 | 2): string {
  const labels: Record<number, string> = {
    0: "Thank You Page",
    1: "Customer Portal (V2)",
    2: "Custom URL",
  };
  return labels[type] || "Unknown";
}

// ============================================================================
// DATE FORMATTING HELPERS
// ============================================================================

/**
 * Convert UTC ISO 8601 string to Date object
 * @param utcString - ISO 8601 date string from API
 * @returns Date object or null if input is null/invalid
 */
export function formatUtcDate(utcString: string | null): Date | null {
  if (!utcString) return null;
  try {
    return new Date(utcString);
  } catch {
    return null;
  }
}

/**
 * Convert Date object to UTC ISO 8601 string for API
 * @param date - Date object
 * @returns ISO 8601 string or null if input is null
 */
export function toUtcString(date: Date | null): string | null {
  return date ? date.toISOString() : null;
}

/**
 * Convert Date object to end-of-day (23:59:59) UTC ISO 8601 string for API
 * Used for expiration dates where the link should expire at the END of the selected day
 * in the site's timezone.
 *
 * @param date - Date object (typically from a date picker, time is ignored)
 * @param siteUtcOffset - Site's UTC offset in hours (e.g., -5 for EST, 0 for UTC)
 *                        If null/undefined, defaults to UTC (offset 0)
 * @returns ISO 8601 string with time set to 23:59:59 in site's timezone, converted to UTC
 *          Returns null if date is null
 *
 * @example
 * // Site has UTC offset of -5 (EST), user selects 2025-11-30
 * // 23:59:59 EST = 04:59:59 UTC next day
 * // Output: "2025-12-01T04:59:59.000Z"
 *
 * @example
 * // Site has UTC offset of 0 (UTC), user selects 2025-11-30
 * // Output: "2025-11-30T23:59:59.000Z"
 */
export function toEndOfDayUtcString(
  date: Date | null,
  siteUtcOffset?: number | null,
): string | null {
  if (!date) return null;

  // Extract year, month, day from the selected date (in local timezone)
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  // Default to UTC (offset 0) if not provided
  const offset = siteUtcOffset ?? 0;

  // Create a UTC date at 23:59:59 for the selected calendar date
  // Then subtract the site's UTC offset to convert site local time to UTC
  // If site is EST (offset -5), 23:59:59 EST = 23:59:59 - (-5) = 04:59:59 UTC next day
  const endOfDayUtc = new Date(
    Date.UTC(year, month, day, 23 - offset, 59, 59, 0),
  );

  return endOfDayUtc.toISOString();
}

/**
 * Convert end-of-day UTC ISO 8601 string from API back to a Date object for the datepicker.
 * This is the reverse of toEndOfDayUtcString - it extracts the original selected date
 * from the stored UTC end-of-day timestamp.
 *
 * @param utcString - ISO 8601 date string from API (e.g., "2025-12-01T04:59:59.000Z")
 * @param siteUtcOffset - Site's UTC offset in hours (e.g., -5 for EST, 0 for UTC)
 *                        If null/undefined, defaults to UTC (offset 0)
 * @returns Date object representing the original selected date for display in datepicker
 *          Returns null if input is null/invalid
 *
 * @example
 * // API returned "2025-12-01T04:59:59.000Z" for site with UTC offset -5 (EST)
 * // This was stored as 23:59:59 EST = 04:59:59 UTC next day
 * // We need to get back the original date: Nov 30, 2025
 * // Output: Date object for Nov 30, 2025 (time set to noon to avoid any edge cases)
 */
export function parseEndOfDayUtcToDate(
  utcString: string | null,
  siteUtcOffset?: number | null,
): Date | null {
  if (!utcString) return null;

  try {
    const utcDate = new Date(utcString);
    if (isNaN(utcDate.getTime())) return null;

    // Default to UTC (offset 0) if not provided
    const offset = siteUtcOffset ?? 0;

    // The UTC date stores 23:59:59 site-local time converted to UTC.
    // To get the original date, we need to:
    // 1. Get the UTC hours from the stored timestamp
    // 2. Add back the site's offset to get site-local hours
    // 3. If site-local hours would be 23, we're on the correct date
    //
    // For EST (offset -5):
    //   UTC hour 4 + (-(-5)) = UTC hour 4 + 5 = 9 (not 23, but we stored it as 23:59:59)
    //   Actually: 23 - (-5) = 28, which is hour 4 next day in UTC
    //   So: UTC hour 4 means site hour = 4 - (-5) = 4 + 5 = -1 = 23 previous day? No...
    //
    // Let me think again:
    // We store: Date.UTC(year, month, day, 23 - offset, 59, 59, 0)
    // For Nov 30 with offset -5: Date.UTC(2025, 10, 30, 23 - (-5), 59, 59, 0)
    //                          = Date.UTC(2025, 10, 30, 28, 59, 59, 0)
    //                          = Dec 1, 2025 04:59:59 UTC
    //
    // To reverse: we have Dec 1, 2025 04:59:59 UTC
    // Apply offset: 04 + (-5) = -1 hour = 23 hours previous day = Nov 30, 23:59:59 site time
    // So the original date is Nov 30

    // Create a new date by adding the offset hours to get site-local time
    // Then extract the date portion
    const siteLocalMs = utcDate.getTime() + offset * 60 * 60 * 1000;
    const siteLocalDate = new Date(siteLocalMs);

    // Extract just the date portion and create a clean Date at noon (to avoid edge cases)
    const year = siteLocalDate.getUTCFullYear();
    const month = siteLocalDate.getUTCMonth();
    const day = siteLocalDate.getUTCDate();

    // Return a date set to noon local time for the datepicker
    return new Date(year, month, day, 12, 0, 0, 0);
  } catch {
    return null;
  }
}

/**
 * Format UTC date string for display (e.g., "Nov 7, 2025")
 * @param utcString - ISO 8601 date string from API
 * @param locale - Locale for formatting (default: 'en-US')
 * @returns Formatted date string or '-' if null/invalid
 */
export function formatUtcDateForDisplay(
  utcString: string | null,
  locale = "en-US",
): string {
  const date = formatUtcDate(utcString);
  if (!date) return "-";

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

/**
 * Format UTC date string for display with time
 * @param utcString - ISO 8601 date string from API
 * @param locale - Locale for formatting (default: 'en-US')
 * @returns Formatted date string with time or '-' if null/invalid
 */
export function formatUtcDateTimeForDisplay(
  utcString: string | null,
  locale = "en-US",
): string {
  const date = formatUtcDate(utcString);
  if (!date) return "-";

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

// ============================================================================
// UTM PARAMS HELPERS
// ============================================================================

/**
 * Parse UTM params JSON string from API
 * @param utmParamsJson - JSON string from API (e.g., '{"utm_source":"email"}')
 * @returns Parsed UtmParams object or null if parsing fails
 */
export function parseUtmParams(utmParamsJson: string | null): UtmParams | null {
  if (!utmParamsJson) return null;
  try {
    return JSON.parse(utmParamsJson) as UtmParams;
  } catch {
    console.error("Failed to parse UTM params JSON:", utmParamsJson);
    return null;
  }
}

/**
 * Stringify UTM params object to JSON string for API
 * @param utmParams - UtmParams object
 * @returns JSON string or null if input is null/empty
 */
export function stringifyUtmParams(utmParams: UtmParams | null): string | null {
  if (!utmParams) return null;

  // Remove empty/undefined values
  const cleaned = Object.entries(utmParams).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      acc[key as keyof UtmParams] = value;
    }
    return acc;
  }, {} as UtmParams);

  // Return null if no valid params
  if (Object.keys(cleaned).length === 0) return null;

  return JSON.stringify(cleaned);
}

/**
 * Format UTM params for display (e.g., "source: email, medium: link")
 * @param utmParamsJson - JSON string from API
 * @returns Human-readable string or '-' if no params
 */
export function formatUtmParamsForDisplay(
  utmParamsJson: string | null,
): string {
  const params = parseUtmParams(utmParamsJson);
  if (!params) return "-";

  const parts: string[] = [];
  if (params.utm_source) parts.push(`Source: ${params.utm_source}`);
  if (params.utm_medium) parts.push(`Medium: ${params.utm_medium}`);
  if (params.utm_campaign) parts.push(`Campaign: ${params.utm_campaign}`);
  if (params.utm_content) parts.push(`Content: ${params.utm_content}`);

  return parts.length > 0 ? parts.join(", ") : "-";
}

// ============================================================================
// USAGE DISPLAY HELPERS
// ============================================================================

/**
 * Format usage count for display (e.g., "47 / 100", "∞", "Single Use")
 * @param usageLimitType - 0=SingleUse, 1=LimitedUse, 2=Unlimited
 * @param currentUsage - Current usage count
 * @param maxUses - Maximum uses allowed
 * @returns Formatted usage string
 */
export function formatUsageDisplay(
  usageLimitType: 0 | 1 | 2,
  currentUsage: number,
  maxUses: number | null,
): string {
  if (usageLimitType === 0) {
    // SingleUse
    return "Single Use";
  } else if (usageLimitType === 1) {
    // Limited use
    return `${currentUsage} / ${maxUses || 0}`;
  } else if (usageLimitType === 2) {
    // Unlimited
    return `${currentUsage} (Unlimited)`;
  }
  return "-";
}

/**
 * Get usage percentage (for limited use links)
 * @param currentUsage - Current usage count
 * @param maxUses - Maximum uses allowed
 * @returns Percentage (0-100) or null if not applicable
 */
export function getUsagePercentage(
  currentUsage: number,
  maxUses: number | null,
): number | null {
  if (!maxUses || maxUses === 0) return null;
  return Math.round((currentUsage / maxUses) * 100);
}

/**
 * Get usage status severity (for progress indicators)
 * @param currentUsage - Current usage count
 * @param maxUses - Maximum uses allowed
 * @returns Severity level for UI indicators
 */
export function getUsageSeverity(
  currentUsage: number,
  maxUses: number | null,
): "success" | "warning" | "danger" {
  const percentage = getUsagePercentage(currentUsage, maxUses);
  if (!percentage) return "success";

  if (percentage >= 100) return "danger"; // Limit reached
  if (percentage >= 80) return "warning"; // Near limit
  return "success"; // Healthy
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Check if quick link is expired
 * @param expiredUtc - ISO 8601 date string from API
 * @returns True if expired, false if not expired or no expiration set
 */
export function isQuickLinkExpired(expiredUtc: string | null): boolean {
  if (!expiredUtc) return false; // No expiration
  const expiredDate = formatUtcDate(expiredUtc);
  if (!expiredDate) return false;
  return expiredDate < new Date();
}

/**
 * Check if quick link has reached usage limit
 * @param usageLimitType - 0=SingleUse, 1=LimitedUse, 2=Unlimited
 * @param currentUsage - Current usage count
 * @param maxUses - Maximum uses allowed
 * @returns True if limit reached, false otherwise
 */
export function hasReachedUsageLimit(
  usageLimitType: 0 | 1 | 2,
  currentUsage: number,
  maxUses: number | null,
): boolean {
  if (usageLimitType === 0) {
    // SingleUse - depends on backend logic
    return currentUsage > 0; // Simplified check
  }
  if (usageLimitType === 1) {
    // Limited use
    return maxUses !== null && currentUsage >= maxUses;
  }
  if (usageLimitType === 2) return false; // Unlimited
  return false;
}

/**
 * Get quick link availability status
 * @param status - 0=Disabled, 1=Enabled, 2=Deleted
 * @param expiredUtc - ISO 8601 date string from API
 * @param usageLimitType - 0=SingleUse, 1=LimitedUse, 2=Unlimited
 * @param currentUsage - Current usage count
 * @param maxUses - Maximum uses allowed
 * @returns Human-readable availability status
 */
export function getQuickLinkAvailabilityStatus(
  status: 0 | 1 | 2,
  expiredUtc: string | null,
  usageLimitType: 0 | 1 | 2,
  currentUsage: number,
  maxUses: number | null,
): {
  available: boolean;
  reason: string;
  severity: "success" | "warning" | "danger" | "secondary";
} {
  // Check if deleted
  if (status === 2) {
    return { available: false, reason: "Deleted", severity: "danger" };
  }

  // Check if disabled
  if (status === 0) {
    return { available: false, reason: "Disabled", severity: "warning" };
  }

  // Check if expired
  if (isQuickLinkExpired(expiredUtc)) {
    return { available: false, reason: "Expired", severity: "danger" };
  }

  // Check if usage limit reached
  if (hasReachedUsageLimit(usageLimitType, currentUsage, maxUses)) {
    return {
      available: false,
      reason: "Usage limit reached",
      severity: "danger",
    };
  }

  // Available
  return { available: true, reason: "Active", severity: "success" };
}
